/* eslint-disable */
import * as Sentry from '@sentry/react'
import actionTypes from '../constants/actionTypes'
import {
  setOrganization,
  setPickerOrganization,
  setPickerOrgUserCount
} from '../slices/organizationSlice'
import {
  setGroupInfo as setGroupInfoAction,
  setGroupMembers,
  setGroupId as setGroupIdAction,
  setOnlyInGroup,
  setMyRole as setMyRoleAction,
  setGroupCustomerConfig as setGroupCustomerConfigAction,
  setAiPhotoTagsState as setAiPhotoTagsStateAction,
  setGroupAuthPolicy as setGroupAuthPolicyAction
} from '../slices/groupSlice'
import fetchHelper from '../utils/fetchHelper'
import sensors, { sensorsRole, sensorsVipLevel } from '../utils/sensors'
import { message, Modal } from 'antd'
import CONFIG from '../constants/config'
import exceptionHelper, { Status } from '../utils/exceptionHelper'
import { captureFetchException } from '../utils/sentryUtils'
import { fetchUserPoliceList } from './main'
import { createDepartmentAuth } from '../app/authPolicyHooks'
import { syncTask as syncTaskAction } from './aiTask'
import { fetchTaskFlowLandingState } from '../slices/taskFlowLandingSlice'

export function setAiPhotoTagsState(data) {
  return setAiPhotoTagsStateAction(data)
}

const parseOutputParam = (outputParam) => {
  if (!outputParam) return null
  try {
    return JSON.parse(outputParam)
  } catch (error) {
    console.warn('解析 outputParam 失败:', error)
    return null
  }
}

export function setGroupId(data) {
  return setGroupIdAction(data)
}

export function setMyRole(data) {
  return setMyRoleAction(data)
}

export function setMembers(data) {
  return setGroupMembers(data)
}

interface GroupInfoOptions {
  /** 通知所属账号，防止 React 提交前已发生登录切换。 */
  expectedUserId?: string
  /** 通知触发的后台刷新可取消，普通 Web 调用无需传入。 */
  signal?: AbortSignal
  /** 只同步团队会员信息，保留当前业务页的加载态、表单和 AI 标签。 */
  membershipOnly?: boolean
}

export function fetchGroupInfo(data, options: GroupInfoOptions = {}) {
  const params = data
  return (dispatch, getState) => {
    const userIdAtStart =
      options.expectedUserId ?? getState().main.user?.user_id
    const checkCurrent = () => {
      if (
        options.signal &&
        (options.signal.aborted ||
          getState().main.user?.user_id !== userIdAtStart ||
          getState().group.groupId !== params.group_id)
      )
        throw new DOMException('Aborted', 'AbortError')
    }
    checkCurrent()
    if (!options.membershipOnly)
      dispatch({
        type: actionTypes.SET_LOADING,
        data: true
      })
    let config = {
      checkCurrent,
      url: __API_SERVER_HOST__ + '/web/workgroup/v3/query/group',
      arguments: {
        signal: options.signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => {
        checkCurrent()
        // 后台会员刷新不走公共选团队弹窗，避免失败时改写普通 Web 的默认团队。
        if (options.membershipOnly) {
          if (result?.code !== 200 || result?.data?.status !== Status.ok)
            throw new Error('团队会员状态刷新失败')
          return result.data
        }
        return exceptionHelper(result)
      })
      .then((data) => {
        checkCurrent()
        dispatch(setGroupInfoAction(data))
        dispatch(setOnlyInGroup(data.only_in_group || 0))
        const groupPolicyList = getState().authPolicy?.groupPolicyList
        if (groupPolicyList && groupPolicyList.length > 0) {
          const groupPolicy = groupPolicyList.find(
            (item) => item.groupID === params.group_id
          )
          if (groupPolicy) {
            dispatch(setGroupAuthPolicyAction(groupPolicy))
            dispatch(setMyRole(groupPolicy.roleID))
            sensors.registerPage({
              role: sensorsRole(groupPolicy.roleID)
            })
          }
        }

        const myRoleUser = data.user_list?.find(
          (user) => user.user_id === getState().main.user?.user_id
        )
        const myRole = myRoleUser?.role_type

        //注册sensors全局属性
        sensors.registerPage({
          groupID: params.group_id,
          isGroupVIP: data.is_vip,
          groupVipLevel: sensorsVipLevel(data)
        })
        sensors.track('web_workgroup_get_group_info', {
          groupName: data.group_name,
          inviteID: data.invite_id,
          role: CONFIG.saKeywords.roles[myRole]
        })

        if (options.membershipOnly) return data

        // 拍照流程 Landing 态并行拉取:
        // 团队信息(含 groupAuthPolicy)已就绪时 fire-and-forget,slice 里自算 landing/dashboard,
        // 结果供 photoTaskFlow 页面直接读,避免用户点开 tab 才发请求闪一下 landing。
        dispatch(fetchTaskFlowLandingState({ groupID: params.group_id }))

        // 同步获取AI智能看图标签状态（与 query/group 同时触发），await 以保证 state 更新完成后再 resolve
        const userId = getState().main.user?.user_id
        const syncPayload: {
          taskName: string
          taskRoute: string
          inputParam: string
          user_id?: string
        } = {
          taskName: 'group_photo_preview',
          taskRoute: 'get_tags',
          inputParam: JSON.stringify({ group_id: params.group_id })
        }
        if (userId) {
          syncPayload.user_id = userId
        }

        return dispatch(syncTaskAction(syncPayload))
          .then((result) => {
            const isOk =
              result?.code === 200 && (result?.data?.status || 0) === 200
            const output = parseOutputParam(
              result?.data?.outputParam || result?.data?.output_param
            )
            const tags =
              isOk && Array.isArray(output?.data?.tags) ? output.data.tags : []
            console.log('aiPhotoTags sync result:', result)
            console.log('aiPhotoTags parsed tags:', tags)
            dispatch(
              setAiPhotoTagsState({
                groupId: params.group_id,
                count: tags.length,
                loaded: true,
                tags
              })
            )
            return data
          })
          .catch(() => {
            dispatch(
              setAiPhotoTagsState({
                groupId: params.group_id,
                count: 0,
                loaded: true,
                tags: []
              })
            )
            return data
          })
          .then((resolvedData) => {
            Sentry.configureScope((scope) => {
              const groupInfo = resolvedData
              delete groupInfo.user_list
              scope.setExtra('GroupInfo', groupInfo)
              scope.setTag('group.invite_id', groupInfo.invite_id)
              scope.setTag('group.id', params.group_id)
            })
            return resolvedData
          })
      })
      .then((data) => {
        // dispatch(fetchUserPoliceList())
        return Promise.resolve(data)
      })
      .catch((err) => {
        checkCurrent()
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() => {
        if (!options.membershipOnly)
          dispatch({
            type: actionTypes.SET_LOADING,
            data: false
          })
      })
  }
}

export function fetchGroupDeparment(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/tree',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        dispatch(setOrganization(data.departmentTree))
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

const expandOrganization = (organization: any, id: any, data: any) => {
  // 深拷贝解冻 RTK immer frozen 对象，确保可修改
  let org = organization ? JSON.parse(JSON.stringify(organization)) : null
  const insertChildrenRecursive = (
    children: any,
    id: any,
    departments: any,
    members?: any
  ) => {
    for (let i = 0; i < children.length; i++) {
      if (children[i].id === id) {
        children[i].children = departments
        children[i].users = members || data.member
        return
      } else if (children[i].children) {
        insertChildrenRecursive(children[i].children, id, departments, members)
      }
    }
  }
  if (org === null || id === 0) {
    org = data.departmentRoute.pop()
    org.children = data.department
    org.users = data.member
  } else {
    insertChildrenRecursive(org.children, id, data.department, data.member)
  }
  return org
}

export function fetchGroupDeparmentByLevel(params) {
  return (dispatch, getState) => {
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/v2/tree',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        const organization = getState().organization.pickerOrganization
        const newOrg = expandOrganization(
          organization,
          params.departmentID || 0,
          data
        )
        dispatch(setPickerOrganization(newOrg))
        if (!params.departmentID || params.departmentID === '0') {
          dispatch(setPickerOrgUserCount(data.userCount))
        }
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
      })
  }
}

export function createDeparment(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/add',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        if (data.status === 0) {
          message.success('操作成功')
        }
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

export function deleteDepartment(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/delete',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        message.success('操作成功')
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

export function renameDepartment(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/name/update',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        message.success('操作成功')
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

export function setUsersDepartment(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/user/set',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

export function addUsersToDepartment(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/user/shift',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        message.success('操作成功')
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

export function batchDeleteUsers(params) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/batch/remove/user',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        if (data.status === Status.removeSubscribedUser) {
          Modal.destroyAll()
          Modal.info({
            title: `成员${data.failedUser[0]}删除失败`,
            content: data.msg
          })
        } else {
          message.success('删除成功')
        }
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

// 设为部门管理员
export function setUserAsDepartmentAdmin(params) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/policy/departmentmanager/create',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        message.success('操作成功')
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

// 取消部门管理员
export function cancelUserAsDepartmentAdmin(params) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/policy/departmentmanager/delete',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        message.success('操作成功')
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() =>
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      )
  }
}

// 导出名单
export function exportUsers(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/user/excel/download',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() => {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      })
  }
}

// 导出记录
export function fetchExportUserHistory(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/user/excel/history',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() => {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      })
  }
}

/**
 * 组织架构成员导入
 * @param {*} data
 */
export function importUsers(data) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/workgroup/batch/invite/department/user',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() => {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      })
  }
}

export function fetchCustomersRules(data) {
  return (dispatch, getState) => {
    let config = {
      url: __API_SERVER_HOST__ + '/web/workgroup/visit_agg_rule/count',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then((result) => exceptionHelper(result))
      .then((data) => {
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
  }
}

export function fetchGroupCustomerConfig(data) {
  const params = data
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    let config = {
      url: __API_SERVER_HOST__ + '/web/group/customer/visit_type/get',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then(exceptionHelper)
      .then((data) => {
        dispatch(setGroupCustomerConfigAction(data))

        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
      .finally(() => {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
      })
  }
}

export function fetchGroupLimitInfo(data) {
  return (dispatch, getState) => {
    let config = {
      url: __API_SERVER_HOST__ + '/web/group/limit/info',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then(exceptionHelper)
      .then((data) => {
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
  }
}

/**
 * 在筛选部门成员时，搜索成员，或从URL参数的member_id创建查询成员。
 * @param {Object} params
 * @param {string} [params.searchKey] - 搜索关键字，用于按姓名或其他关键属性匹配成员。
 * @param {string} [params.searchUserID] - 可选的用户ID，用于精准匹配某个用户。
 * @param {0 | 1 | 2} [params.scope] - 搜索范围，0-全部, 1-已经退出, 2-仅未退出。
 * @param {string} [params.scene] - 搜索场景，如果是'department_manage', 会返回成员的部门、手机号、上次登录时间
 * @returns {Promise}
 */
export function searchDepartmentMember(params) {
  return (dispatch, getState) => {
    const group = getState().group
    const userID = getState().main.user?.user_id
    const onlyInGroup = group.onlyInGroup
    const departmentAuthPolicy = createDepartmentAuth(group, userID)
    let config = {
      url: __API_SERVER_HOST__ + '/web/department/search/user',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userID,
          groupID: group.groupId,
          ...departmentAuthPolicy,
          scope: onlyInGroup === CONFIG.onlyInGroup.ONLY_IN_GROUP ? 2 : 0,
          ...params
        })
      }
    }
    return fetchHelper(dispatch, getState, config)
      .then(exceptionHelper)
      .then((data) => {
        return Promise.resolve(data)
      })
      .catch((err) => {
        captureFetchException(err, config)
        return Promise.reject(null)
      })
  }
}
